import axios from 'axios';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { OAuthAuthorizeParamsSchema } from 'shared';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useMe } from '../features/auth/queries';
import { useOAuthDecision } from '../features/oauth/mutations';
import { useOAuthAuthorizePreview } from '../features/oauth/queries';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<{ error?: { message?: string } }>(error)) {
    return error.response?.data.error?.message ?? fallback;
  }
  return fallback;
}

function ConsentLayout({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-8 text-text">
      {children}
    </main>
  );
}

export function OAuthConsent() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useMemo(() => {
    const parsed = OAuthAuthorizeParamsSchema.safeParse(Object.fromEntries(searchParams));
    return parsed.success ? parsed.data : null;
  }, [searchParams]);
  const me = useMe();
  const preview = useOAuthAuthorizePreview(me.isSuccess ? params : null);
  const decision = useOAuthDecision();

  if (me.isLoading) {
    return <ConsentLayout>Loading session...</ConsentLayout>;
  }

  if (me.isError || !me.data) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (!params || preview.isError) {
    return (
      <ConsentLayout>
        <Card className="w-full max-w-md" role="alert">
          <h1 className="text-xl font-semibold">Connection request not valid</h1>
          <p className="mt-2 text-sm text-text-muted">
            {params
              ? apiErrorMessage(preview.error, 'This request could not be verified.')
              : 'The link is missing required details.'}{' '}
            Start the connection again from your AI app.
          </p>
        </Card>
      </ConsentLayout>
    );
  }

  if (preview.isLoading || !preview.data) {
    return <ConsentLayout>Checking connection request...</ConsentLayout>;
  }

  const { clientName, redirectHost } = preview.data;
  const pending = decision.isPending || decision.isSuccess;

  return (
    <ConsentLayout>
      <Card
        className="w-full max-w-md"
        elevation="primary"
        header={
          <div>
            <h1 className="text-xl font-semibold">Connect {clientName}</h1>
            <p className="mt-1 text-sm text-text-muted">Signed in as {me.data.email}</p>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            <span className="font-medium">{clientName}</span> is asking for{' '}
            <span className="font-medium">read-only</span> access to your accounts, positions, trade
            plans, rules, reviews, analytics and market console.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-text-muted">
            <li>It cannot change rules, plans, stops or positions.</li>
            <li>It cannot place trades.</li>
            <li>You can disconnect it at any time in Settings.</li>
          </ul>
          <p className="text-text-muted">
            After you choose, you will be sent back to{' '}
            <span className="font-medium text-text">{redirectHost}</span>.
          </p>
          {decision.isError ? (
            <p className="text-danger" role="alert">
              {apiErrorMessage(decision.error, 'Could not complete the request. Try again.')}
            </p>
          ) : null}
        </div>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            className="sm:flex-1"
            disabled={pending}
            onClick={() => decision.mutate({ params, approve: true })}
          >
            Allow read-only access
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="sm:flex-1"
            disabled={pending}
            onClick={() => decision.mutate({ params, approve: false })}
          >
            Deny
          </Button>
        </div>
      </Card>
    </ConsentLayout>
  );
}
